import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Editor } from '@tinymce/tinymce-react';
import Button from '../common/Button';
import { differenceInHours } from 'date-fns'; // Função para calcular a diferença de horas

function ObservacaoModal({
  isOpen,
  onClose,
  value,
  onChange,
  onSave,
  handleAutoFill, // Função para autopreencher as observações
  handleClearObservacao, // Função para limpar as observações
  callerComponent, // Componente que chamou o modal
  isReadOnly, // Nova prop para definir se o modal está em modo somente leitura
  timestampAtendimento, // Data de atendimento para calcular o tempo restante
}) {
  const [isEditorLoading, setIsEditorLoading] = useState(true); // Estado para controlar o carregamento do editor
  const [isEditable, setIsEditable] = useState(true); // Estado para bloquear/desbloquear edição
  const [remainingTime, setRemainingTime] = useState(null); // Armazena o tempo restante

  // Função para calcular o tempo restante
  useEffect(() => {
    if (timestampAtendimento) {
      const atendimentoDate = new Date(timestampAtendimento);
      const hoursPassed = differenceInHours(new Date(), atendimentoDate);

      if (hoursPassed >= 24) {
        setIsEditable(false); // Bloqueia edição após 24 horas
      } else {
        setRemainingTime(24 - hoursPassed); // Define o tempo restante em horas
        setIsEditable(true); // Permite edição
      }
    }
  }, [timestampAtendimento]);

  if (!isOpen) return null;

  // Função para determinar se um botão deve ser exibido
  const shouldShowButton = (buttonType) => {
    // Se estiver em modo somente leitura ou tempo de edição expirou, não exibir botões
    if (isReadOnly || !isEditable) return false;

    const componentsToHideButtons = [
      'PacienteModal',
      'PatientEditModal',
      'HistoricoAtendimentos',
    ];

    if (componentsToHideButtons.includes(callerComponent)) {
      return (
        buttonType !== 'autopreencher' &&
        buttonType !== 'limpar' &&
        buttonType !== 'salvar'
      );
    }
    // Exibir todos os botões como padrão
    return true;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl p-6 relative">
        <div className="flex justify-between items-center border-b pb-4 mb-4">
          <h2 className="text-xl font-semibold text-gray-700">
            {isReadOnly || !isEditable
              ? 'Visualizar Anamnese'
              : 'Anamnese/Observações'}
          </h2>
          <Button type="iconFechar" label="X" onClick={onClose} />
        </div>

        {/* Exibe o temporizador se ainda houver tempo restante */}
        {remainingTime !== null && isEditable && (
          <div className="text-gray-500 text-sm mb-4">
            Tempo restante para edição: {remainingTime} horas
          </div>
        )}

        {/* Exibe o spinner enquanto o TinyMCE não estiver completamente renderizado */}
        {isEditorLoading && (
          <div className="flex justify-center items-center h-64">
            <div className="w-16 h-16 border-4 border-primary border-dashed rounded-full animate-spin"></div>
          </div>
        )}

        {/* Renderiza o editor apenas quando o quadro de edição estiver carregado */}
        <div className={isEditorLoading ? 'hidden' : ''}>
          <Editor
            value={value}
            onEditorChange={onChange}
            apiKey="3vkvusz5hfvjrpeauwgdujxxjn046kgfkoi5eq3rwmi65ney"
            init={{
              height: 500,
              menubar: !isReadOnly && isEditable, // Desativar o menubar se estiver em modo de leitura ou tempo expirado
              language: 'pt_BR',
              plugins: [
                'autolink',
                'lists',
                'link',
                'image',
                'charmap',
                'preview',
                'anchor',
                'searchreplace',
                'visualblocks',
                'fullscreen',
                'insertdatetime',
                'media',
                'table',
                'wordcount',
              ],
              toolbar:
                isReadOnly || !isEditable
                  ? false // Se for modo de leitura ou tempo expirado, ocultar a toolbar
                  : 'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat',
              readonly: isReadOnly || !isEditable, // Se estiver em modo de leitura ou tempo expirado, ativar o modo readonly
              skin_url: `${process.env.PUBLIC_URL}/tinymce/skins/ui/oxide`,
              content_css: `${process.env.PUBLIC_URL}/tinymce/skins/content/default`,
              branding: false,
              promotion: false,
              setup: (editor) => {
                editor.on('init', () => {
                  setIsEditorLoading(false); // Desabilitar o spinner quando o editor estiver totalmente carregado
                });
              },
            }}
          />
        </div>

        {/* Ajuste de responsividade nos botões */}
        <div className="mt-4 flex flex-wrap gap-4 justify-end sm:justify-start">
          {shouldShowButton('autopreencher') && (
            <Button
              type="autopreencher"
              label="Autopreencher"
              onClick={handleAutoFill}
              className="w-full sm:w-auto"
            />
          )}
          {shouldShowButton('limpar') && (
            <Button
              type="limpar"
              label="Limpar Observações"
              onClick={handleClearObservacao}
              className="w-full sm:w-auto"
            />
          )}
          {shouldShowButton('salvar') && (
            <Button
              type="salvar"
              label="Salvar Observação"
              onClick={onSave}
              className="w-full sm:w-auto"
            />
          )}
        </div>
      </div>
    </div>
  );
}

ObservacaoModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  handleAutoFill: PropTypes.func.isRequired, // Propriedade para o autopreencher
  handleClearObservacao: PropTypes.func.isRequired, // Propriedade para limpar
  callerComponent: PropTypes.string.isRequired, // Componente que chamou o modal
  isReadOnly: PropTypes.bool, // Nova prop para definir se o modal está em modo somente leitura
  timestampAtendimento: PropTypes.string.isRequired, // Passa o timestamp do atendimento para cálculo do tempo restante
};

export default ObservacaoModal;
